import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Home, Plus, MessageCircle, X, Heart, PartyPopper, Lightbulb, ShoppingBag, Car, Briefcase, Compass, Users, Building2, Lock } from 'lucide-react';
import { useUserStore } from '../stores/userStore';
import { Conversation } from '../api/chatService';
import { chatService } from '../api/chatService';
import { BusinessApiService } from '../api/businessApi';

const DashboardBottomNav: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useUserStore();
    const [showPostMenu, setShowPostMenu] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [businessStatus, setBusinessStatus] = useState<string | null>(null);
    const [businessPlan, setBusinessPlan] = useState<string | null>(null);

    useEffect(() => {
        if (!user?.is_employer) return;
        let cancelled = false;
        BusinessApiService.getUserBusinesses()
            .then((res) => {
                if (cancelled) return;
                if (res.success && res.data && res.data.length > 0) {
                    const primary = res.data.find((b) => b.status === 'approved') || res.data[0];
                    setBusinessStatus(primary.status);
                    setBusinessPlan(primary.employer_plan ?? null);
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user?.is_employer]);

    const POST_OPTIONS = [
      { icon: <Heart className="w-5 h-5" />, label: t('bottom_nav.post_opt_community_help_label'), description: t('bottom_nav.post_opt_community_help_desc'), path: '/community-help', color: 'text-green-500' },
      { icon: <Car className="w-5 h-5" />, label: t('bottom_nav.post_opt_rides_label'), description: t('bottom_nav.post_opt_rides_desc'), path: '/rides-carpools', color: 'text-indigo-500' },
      { icon: <PartyPopper className="w-5 h-5" />, label: t('bottom_nav.post_opt_hangout_label'), description: t('bottom_nav.post_opt_hangout_desc'), path: '/meetups', color: 'text-purple-500' },
      { icon: <Lightbulb className="w-5 h-5" />, label: t('bottom_nav.post_opt_tips_label'), description: t('bottom_nav.post_opt_tips_desc'), path: '/tips-trips-advice', color: 'text-yellow-500' },
      { icon: <ShoppingBag className="w-5 h-5" />, label: t('bottom_nav.post_opt_marketplace_label'), description: t('bottom_nav.post_opt_marketplace_desc'), path: '/marketplace', color: 'text-pink-500' },
    ];

    // Poll conversations for unread badge (uses polling instead of WebSocket
    // to avoid overwriting ChatList's websocketService subscription)
    useEffect(() => {
        if (!user?.uid) return;

        const computeUnread = (conversations: Conversation[]) => {
            let total = 0;
            for (const conv of conversations) {
                const count = conv.unreadCount?.[user.uid] || 0;
                if (count > 0) total += count;
            }
            setUnreadCount(total);
        };

        chatService.getConversations()
            .then(computeUnread)
            .catch(() => {});

        const interval = setInterval(() => {
            chatService.getConversations()
                .then(computeUnread)
                .catch(() => {});
        }, 30000);

        return () => {
            clearInterval(interval);
        };
    }, [user?.uid]);

    const path = location.pathname;
    const isHomeActive = path === '/dashboard';
    const isDiscoverActive = path.startsWith('/discover');
    const isMessagesActive = path.startsWith('/chat');
    const isJobsActive = path.startsWith('/search-jobs') || path.startsWith('/job');

    const itemClass = "flex flex-1 min-w-0 flex-col items-center justify-center min-h-[44px] gap-0.5 transition-colors px-1";
    const labelClass = "text-[10px] sm:text-[11px] font-medium leading-tight max-w-full truncate";
    const iconClass = "h-5 w-5 sm:h-6 sm:w-6";

    if (user?.is_employer) {
        const isEmpDashActive = path === '/employer-dashboard';
        const isPostJobActive = path === '/post-job';
        const isWorkersActive = path === '/employer-browse-workers';
        const isEmpProfileActive = path === '/employer-profile';

        const isApproved = businessStatus === 'approved';
        const isStandardPlus = ['standard', 'pro', 'founding'].includes(businessPlan || '');
        const postJobLocked = !isApproved;
        const workersLocked = !isApproved || !isStandardPlus;

        const handleLocked = (reason: string) => {
            toast(reason, { icon: '🔒' });
        };

        return (
            <nav
                className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 md:hidden z-50"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                <div className="flex items-center justify-between w-full py-2 px-1">
                    <button
                        onClick={() => navigate('/employer-dashboard')}
                        className={`${itemClass} ${isEmpDashActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        aria-label={t('bottom_nav.dashboard')}
                    >
                        <Briefcase className={iconClass} />
                        <span className={labelClass} aria-hidden="true">{t('bottom_nav.dashboard')}</span>
                    </button>
                    {postJobLocked ? (
                        <button
                            onClick={() => handleLocked(t('bottom_nav.locked_pending_approval'))}
                            className={`${itemClass} text-gray-300 cursor-not-allowed`}
                            aria-label={t('bottom_nav.post_job_locked_aria')}
                        >
                            <div className="relative">
                                <Plus className={iconClass} />
                                <Lock className="w-3 h-3 absolute -bottom-0.5 -right-0.5" />
                            </div>
                            <span className={labelClass} aria-hidden="true">{t('bottom_nav.post_job')}</span>
                        </button>
                    ) : (
                        <button
                            onClick={() => navigate('/post-job')}
                            className={`${itemClass} ${isPostJobActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                            aria-label={t('bottom_nav.post_job')}
                        >
                            <Plus className={iconClass} />
                            <span className={labelClass} aria-hidden="true">{t('bottom_nav.post_job')}</span>
                        </button>
                    )}
                    {workersLocked ? (
                        <button
                            onClick={() =>
                                handleLocked(
                                    !isApproved
                                        ? t('bottom_nav.locked_pending_approval')
                                        : t('bottom_nav.locked_upgrade_plan'),
                                )
                            }
                            className={`${itemClass} text-gray-300 cursor-not-allowed`}
                            aria-label={t('bottom_nav.workers_locked_aria')}
                        >
                            <div className="relative">
                                <Users className={iconClass} />
                                <Lock className="w-3 h-3 absolute -bottom-0.5 -right-0.5" />
                            </div>
                            <span className={labelClass} aria-hidden="true">{t('bottom_nav.workers')}</span>
                        </button>
                    ) : (
                        <button
                            onClick={() => navigate('/employer-browse-workers')}
                            className={`${itemClass} ${isWorkersActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                            aria-label={t('bottom_nav.workers')}
                        >
                            <Users className={iconClass} />
                            <span className={labelClass} aria-hidden="true">{t('bottom_nav.workers')}</span>
                        </button>
                    )}
                    <button
                        onClick={() => navigate('/employer-profile')}
                        className={`${itemClass} ${isEmpProfileActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        aria-label={t('bottom_nav.profile')}
                    >
                        <Building2 className={iconClass} />
                        <span className={labelClass} aria-hidden="true">{t('bottom_nav.profile')}</span>
                    </button>
                </div>
            </nav>
        );
    }

    return (<>
        <nav
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 md:hidden z-50 overflow-hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
            <div className="flex items-center justify-between w-full py-2 px-1">
                <button
                    onClick={() => navigate('/dashboard')}
                    className={`${itemClass} ${isHomeActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                    aria-label={t('bottom_nav.home')}
                >
                    <Home className={iconClass} />
                    <span className={labelClass} aria-hidden="true">{t('bottom_nav.home')}</span>
                </button>

                <button
                    onClick={() => navigate('/discover')}
                    className={`${itemClass} ${isDiscoverActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                    aria-label={t('bottom_nav.discover')}
                >
                    <Compass className={iconClass} />
                    <span className={labelClass} aria-hidden="true">{t('bottom_nav.discover')}</span>
                </button>

                <button
                    onClick={() => setShowPostMenu(true)}
                    className="flex flex-1 min-w-0 flex-col items-center justify-center relative z-10 group"
                    aria-label={t('bottom_nav.post')}
                >
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center group-active:scale-95 transition-transform mb-1">
                        <Plus className="h-5 w-5 text-white" />
                    </div>
                    <span className={`${labelClass} text-gray-600`}>{t('bottom_nav.post')}</span>
                </button>

                <button
                    onClick={() => navigate('/search-jobs')}
                    className={`${itemClass} ${isJobsActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                    aria-label={t('bottom_nav.jobs')}
                >
                    <Briefcase className={iconClass} />
                    <span className={labelClass} aria-hidden="true">{t('bottom_nav.jobs')}</span>
                </button>

                <button
                    onClick={() => {
                        if (location.pathname.startsWith('/chat/')) {
                            navigate('/chat', { replace: true });
                        } else if (location.pathname !== '/chat') {
                            navigate('/chat');
                        }
                    }}
                    className={`${itemClass} relative ${isMessagesActive ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                    aria-label={unreadCount > 0 ? t('bottom_nav.messages_unread_aria', { count: unreadCount }) : t('bottom_nav.messages')}
                >
                    <div className="relative">
                        <MessageCircle className={iconClass} />
                        {unreadCount > 0 && (
                            <span aria-hidden="true" className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </div>
                    <span className={labelClass} aria-hidden="true">{t('bottom_nav.messages')}</span>
                </button>
            </div>
        </nav>

      {/* Post Menu Bottom Sheet */}
      {showPostMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setShowPostMenu(false)}
          />
          <div className="fixed z-50 bg-white shadow-2xl pb-8 pt-2 inset-x-0 bottom-0 rounded-t-2xl md:bottom-auto md:top-1/2 md:left-1/2 md:right-auto md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-2xl md:max-w-md md:w-full md:pb-5">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4 md:hidden" />
            <div className="flex items-center justify-between px-5 mb-4">
              <span className="text-base font-bold text-gray-900">{t('bottom_nav.create')}</span>
              <button
                onClick={() => setShowPostMenu(false)}
                className="p-1.5 rounded-full hover:bg-gray-100 transition"
                aria-label={t('bottom_nav.close')}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-3">
              {POST_OPTIONS.map((opt) => (
                <button
                  key={opt.path}
                  onClick={() => { setShowPostMenu(false); navigate(opt.path); }}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition text-left"
                >
                  <div className={`${opt.color} w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center shrink-0`}>
                    {opt.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{opt.label}</div>
                    <div className="text-xs text-gray-500">{opt.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>);
};

export default DashboardBottomNav;
